import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  useBillingAwareness,
  useTrialWidgetEnabled,
  type BillingAwareness,
} from '~/store/entitlements/useTrial';

import TrialBubble, { type TrialTier } from '../../molecules/TrialBubble/view';
import TrialUpgradeSheet from '../../molecules/TrialUpgradeSheet/view';

const DISMISS_KEY = 'pa:trial-widget:dismissed';

interface BubbleLook {
  primary: string;
  secondary: string;
  tier: TrialTier;
}

function look(a: BillingAwareness): BubbleLook {
  const d = a.daysRemaining;
  switch (a.state) {
    case 'trial_active':
      return {
        primary: d <= 0 ? 'Último' : String(d),
        secondary: d === 1 || d <= 0 ? 'dia' : 'dias',
        tier: d <= 3 ? 'soon' : 'calm',
      };
    case 'trial_ending':
      return {
        primary: d <= 0 ? 'Último' : String(d),
        secondary: d === 1 ? 'dia' : 'dias',
        tier: 'urgent',
      };
    case 'trial_ended':
      return { primary: 'Fim', secondary: 'teste', tier: 'urgent' };
    case 'cancelled':
      return { primary: 'Plano', secondary: 'inativo', tier: 'soon' };
    case 'expired':
      return { primary: 'Plano', secondary: 'vencido', tier: 'urgent' };
    case 'no_plan':
      return { primary: 'Premium', secondary: 'ativar', tier: 'calm' };
  }
}

export default function TrialAwarenessWidget() {
  const enabled = useTrialWidgetEnabled();
  const awareness = useBillingAwareness();
  const location = useLocation();
  const navigate = useNavigate();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  const onSubscribeScreen = location.pathname.startsWith('/app/subscribe');
  const eligible =
    enabled &&
    awareness != null &&
    location.pathname.startsWith('/app') &&
    !onSubscribeScreen;

  useEffect(() => {
    if (eligible) return;
    try {
      sessionStorage.removeItem(DISMISS_KEY);
    } catch {
    }
    setSheetOpen(false);
    setDismissed((prev) => (prev ? false : prev));
  }, [eligible]);

  if (!eligible || dismissed || !awareness) return null;

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
    }
    setDismissed(true);
  };

  const handleUpgrade = () => {
    setSheetOpen(false);
    navigate('/app/subscribe');
  };

  const { primary, secondary, tier } = look(awareness);

  return (
    <>
      <TrialBubble
        primaryLabel={primary}
        secondaryLabel={secondary}
        tier={tier}
        onOpen={() => setSheetOpen(true)}
        onDismiss={handleDismiss}
      />
      <TrialUpgradeSheet
        open={sheetOpen}
        state={awareness.state}
        daysRemaining={awareness.daysRemaining}
        onClose={() => setSheetOpen(false)}
        onUpgrade={handleUpgrade}
      />
    </>
  );
}
